import { vintageUrl } from '@helpers/vivinoUrls';
import { useBreakpoint } from '@vivino/js-react-common-ui';
import { Anchor, ChevronDown, Separator, VivinoRating } from '@vivino/js-web-common';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { CartItemSourceContext } from 'vivino-js/context/CartItemSourceContext/CartItemSourceContext';
import { getMedianAmount, isMedianTypeMarket } from 'vivino-js/helpers/availability';
import { getCartItemSource, getCartItemSourceProps } from 'vivino-js/helpers/cartItemSources';
import { isPPCPrice, isVivinoPrice } from 'vivino-js/helpers/price';
import t from 'vivino-js/translationString';
import Price from 'vivino-ui/atoms/Price/Price';
import HighlightIcons from 'vivino-ui/icons/Highlights/36';

import { Col, Row } from 'vivino-ui/components/Grid';
import Highlight from 'vivino-ui/components/Highlight';

import styles from './vintageListRow.scss';

const TRANSLATIONS_PATH_AVAILABILITY = 'components.deal_wine_page.purchase_availability';
const TRANSLATIONS_PATH_RATING = 'components.shared.vivino_rating';

const TRANSLATIONS = {
  availableForPurchase: `${TRANSLATIONS_PATH_AVAILABILITY}.available_for_purchase`,
  onlyAvailableFromExternalShops: `${TRANSLATIONS_PATH_AVAILABILITY}.only_available_from_external_shops`,
  noAveragePriceAvailable: `${TRANSLATIONS_PATH_AVAILABILITY}.no_average_price_available`,
  notEnoughRatingsYet: `${TRANSLATIONS_PATH_RATING}.not_enough_ratings_yet`,
};

const VintageListRow = ({ vintage, highlight, availability, currency, isInModal = false }) => {
  const price = availability?.price;
  const isOnVivino = isVivinoPrice(price) || price?.type === 'linkout';
  const isPPCAndMedianTypeMarket = isPPCPrice(price) && isMedianTypeMarket(availability);

  let availabilityMessage = t(TRANSLATIONS.noAveragePriceAvailable);
  let amount;

  const cartItemSourceType = useContext(CartItemSourceContext);
  const cartItemSourceData = getCartItemSource(cartItemSourceType);

  if (isOnVivino) {
    amount = price?.amount;
    availabilityMessage = t(TRANSLATIONS.availableForPurchase);
  } else if (isPPCAndMedianTypeMarket) {
    amount = getMedianAmount(availability);
    availabilityMessage = t(TRANSLATIONS.onlyAvailableFromExternalShops);
  }

  const { isDesktop, isTablet, isMobile } = useBreakpoint();

  return (
    <Anchor
      href={vintageUrl({ vintage, price })}
      className={styles.anchor}
      rel="nofollow"
      dataTestId={'vintage-page-link'}
      {...getCartItemSourceProps(cartItemSourceData)}
    >
      <Row className={styles.vintageListRow}>
        <Col desktop={1} tablet={2} mobile={1}>
          <span className={styles.year}>{vintage?.year}</span>
        </Col>

        {!isMobile && (
          <Col desktop={3} tablet={4}>
            {highlight && <Highlight highlight={highlight} shouldLink={false} />}
          </Col>
        )}
        <Col desktop={isInModal ? 2 : 4} tablet={3} mobile={3} className={styles.ratingsContainer}>
          {isMobile && (
            <div className={styles.highlight}>
              {highlight && (
                <HighlightIcons className={styles.icon} highlightType={highlight.highlight_type} />
              )}
            </div>
          )}
          {vintage?.has_valid_ratings ? (
            <VivinoRating vintage={vintage} />
          ) : (
            t(TRANSLATIONS.notEnoughRatingsYet)
          )}
        </Col>
        {isDesktop && (
          <Col desktop={isInModal ? 2 : 3}>
            <div
              className={cx(styles.availability, {
                [styles.vivinoPrice]: isOnVivino,
              })}
            >
              {availabilityMessage}
            </div>
          </Col>
        )}
        <Col desktop={1} tablet={3} mobile={2} className={styles.price}>
          <div
            className={cx(styles.wrapper, {
              [styles.noPrice]: !price,
            })}
          >
            <>
              {price && (
                <Price
                  amount={amount}
                  currency={currency}
                  className={cx(styles.wrapper, {
                    [styles.vivinoPrice]: isOnVivino,
                  })}
                />
              )}
              {!price && isTablet && <>{availabilityMessage}</>}
            </>
            {!isDesktop && (
              <div>
                <ChevronDown className={styles.chevron} />
              </div>
            )}
          </div>
        </Col>
        <Col desktop={isInModal ? 10 : 12} tablet={12} mobile={6}>
          <Separator className={styles.separator} />
        </Col>
      </Row>
    </Anchor>
  );
};

VintageListRow.propTypes = {
  vintage: PropTypes.object,
  highlight: PropTypes.object,
  availability: PropTypes.object,
  currency: PropTypes.object,
  isInModal: PropTypes.bool,
};

export default VintageListRow;
